import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Form from '../components/Form';
import apiService from '../services/api';
import { ApiError, ApiErrorDetail, FormField, SchedulerSettings } from '../types/api';

// Global, admin-only scheduler settings backed by sb-express-app's
// GET/PUT /admin/scheduler-settings (spec 18). The 1-5 integer range below
// mirrors the backend's Zod validator so out-of-range values never leave
// the browser; the backend's own 400 is still surfaced if one slips through.

const COPY = {
  title: 'Scheduler Settings',
  subtitle: 'Global settings applied to appointment booking for every doctor',
  cardTitle: 'Appointment capacity',
  currentValueLabel: 'Current value:',
  loading: 'Loading scheduler settings...',
  loadFailed: 'Failed to load scheduler settings.',
  saveFailed: 'Failed to save scheduler settings. Please try again.',
  forbidden: 'You do not have permission to manage scheduler settings.',
  saved: 'Scheduler settings saved',
  retry: 'Retry',
  save: 'Save',
};

const FIELD_NAME = 'appointmentCountPerSlot';

const fields: FormField[] = [
  {
    name: FIELD_NAME,
    label: 'Appointment count per slot',
    type: 'number',
    required: true,
    // Whole numbers only; min/max give the specific range errors.
    validation: { pattern: /^\d+$/, min: 1, max: 5 },
  },
];

interface DisplayedError {
  message: string;
  details: ApiErrorDetail[];
}

// Errors arrive already normalized into an ApiError by api.ts's response
// interceptor (a network failure becomes a generic status-500 ApiError).
// Only a 400's message is specific enough to show verbatim; anything else
// gets fixed copy rather than the interceptor's generic 'An error occurred'.
const describeError = (error: unknown, fallback: string): DisplayedError => {
  const { status, message, details }: Partial<ApiError> =
    typeof error === 'object' && error !== null ? (error as Partial<ApiError>) : {};

  if (status === 403) {
    return { message: COPY.forbidden, details: [] };
  }
  if (status === 400 && typeof message === 'string' && message) {
    return { message, details: details ?? [] };
  }
  return { message: fallback, details: [] };
};

const ErrorBanner: React.FC<{ error: DisplayedError }> = ({ error }) => (
  <div className="error mb-4" role="alert">
    <p>{error.message}</p>
    {error.details.length > 0 && (
      <ul>
        {error.details.map((detail) => (
          <li key={`${detail.path}:${detail.message}`}>{detail.message}</li>
        ))}
      </ul>
    )}
  </div>
);

const SchedulerSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SchedulerSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<DisplayedError | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<DisplayedError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setSettings(await apiService.getSchedulerSettings());
    } catch (e: unknown) {
      setLoadError(describeError(e, COPY.loadFailed));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Form re-syncs its local state whenever initialData's identity changes,
  // so derive it from the persisted value only — a failed save must not
  // wipe what the admin typed.
  const initialData = useMemo(
    () => (settings ? { [FIELD_NAME]: settings.appointment_count_per_slot } : {}),
    [settings],
  );

  const handleSave = async (formData: Record<string, unknown>) => {
    try {
      setSaving(true);
      setSaveError(null);
      setSuccess(null);
      const saved = await apiService.updateSchedulerSettings({
        appointment_count_per_slot: Number(formData[FIELD_NAME]),
      });
      setSettings(saved);
      setSuccess(COPY.saved);
    } catch (e: unknown) {
      setSaveError(describeError(e, COPY.saveFailed));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{COPY.title}</h2>
        <p className="text-gray-600">{COPY.subtitle}</p>
      </div>

      {loading && <div className="loading">{COPY.loading}</div>}

      {!loading && loadError && (
        <div>
          <ErrorBanner error={loadError} />
          <button className="btn btn-secondary" onClick={loadSettings}>
            {COPY.retry}
          </button>
        </div>
      )}

      {!loading && settings && (
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="card-title">{COPY.cardTitle}</h3>
          </div>
          <div className="card-content">
            {saveError && <ErrorBanner error={saveError} />}
            {success && <div className="success mb-4">{success}</div>}

            <p className="text-gray-600 mb-4">
              {COPY.currentValueLabel}{' '}
              <strong data-testid="current-appointment-count">{settings.appointment_count_per_slot}</strong>
            </p>

            <Form
              fields={fields}
              onSubmit={handleSave}
              loading={saving}
              initialData={initialData}
              submitText={COPY.save}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulerSettingsPage;
